import { useState, useEffect, useCallback } from 'react'
import { useRecoilState } from 'recoil'
import clsx from 'clsx'
import Decimal from 'decimal.js'
import TabNavigation from '@/components/TabNavigation'
import ConnectWalletView from '@/components/ConnectWalletView'
import IconDiscord from '@/components/icons/IconDiscord'
import IconGlobe from '@/components/icons/IconGlobe'
import IconMedium from '@/components/icons/IconMedium'
import IconTwitter from '@/components/icons/IconTwitter'
import IconColorTradingVolume from '@/components/icons/IconColorTradingVolume'
import IconColorUser from '@/components/icons/IconColorUser'
// import AccountApiBlock from '@/components/account/AccountApiBlock'
import WalletAddressBlock from '@/components/account/WalletAddressBlock'
import MemberTierPopup from '@/components/account/MemberTierPopup'
import ApiTradingKeyPopup from '@/components/account/ApiTradingKeyPopup'
import {
  accountInfoState,
  accountTradingVolumeInfoState,
  feeProgramMaxTierState,
  feeProgramInfoMapState,
  futuresFeeProgramMaxTierState,
  futuresFeeProgramInfoMapState
} from '@/recoil/AccountInfo'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import fetcher from '@/api/fetcher'
import numberFormat from '@/utils/numberFormat'
import './Account.css'

const MARKET_TYPE_LIST = ['SPOT', 'FUTURES']
const MARKET_TYPE_LABEL_MAP = {
  ALL: 'All',
  SPOT: 'Spot',
  FUTURES: 'Futures'
}
const TRADING_VOLUME_MARKET_TYPE_LIST = ['ALL', 'SPOT', 'FUTURES']
const TRADING_VOLUME_INFO_LIST = ['24_HOURS', '30_DAYS', 'YTD']
const TRADING_VOLUME_INFO_LABEL_MAP = {
  '24_HOURS': '24H',
  '30_DAYS': '30 Days'
}
const SOCIAL_ICON_MAP = {
  discord: IconDiscord,
  medium: IconMedium,
  twitter: IconTwitter,
  website: IconGlobe
}
const TRADING_VOLUME_COMPACT_THRESHOLD = 100_000

function format(data, formatKey = 0, compact = true) {
  return numberFormat(data, {
    formatKey,
    defaultValue: '-',
    compact
  })
}

function SocialIconLink({ type, link }) {
  const IconComponent = SOCIAL_ICON_MAP[type]
  return (
    <a className="social-link" target="_blank" href={link} rel="noreferrer">
      <IconComponent />
    </a>
  )
}

export default function Account() {
  const { accountId, walletReady } = useWalletSelector()

  const [accountInfo, setAccountInfo] = useRecoilState(accountInfoState)
  const [tradingVolumeInfo, setTradingVolumeInfo] = useRecoilState(
    accountTradingVolumeInfoState
  )
  const [feeProgramMaxTier] = useRecoilState(feeProgramMaxTierState)
  const [feeProgramInfoMap, setFeeProgramInfoMap] = useRecoilState(
    feeProgramInfoMapState
  )
  const [futuresFeeProgramMaxTier] = useRecoilState(
    futuresFeeProgramMaxTierState
  )
  const [futuresFeeProgramInfoMap, setFuturesFeeProgramInfoMap] =
    useRecoilState(futuresFeeProgramInfoMapState)

  const [showMemberTierPopup, setShowMemberTierPopup] = useState(false)
  const [showApiTradingKeyPopup, setShowApiTradingKeyPopup] = useState(false)
  const [tradingVolumeMarketType, setTradingVolumeMarketType] = useState(
    TRADING_VOLUME_MARKET_TYPE_LIST[0]
  )
  const [memberTierMarketType, setMemberTierMarketType] = useState(
    MARKET_TYPE_LIST[0]
  )

  const feeDisplayAdapter = useCallback(
    fee => (accountInfo.tier && fee ? fee : '-'),
    [accountInfo.tier]
  )

  useEffect(() => {
    if (!walletReady) {
      return
    }

    fetcher.getAccountInfo().then(setAccountInfo)
    fetcher.getAccountTradingVolumeInfo().then(setTradingVolumeInfo)
    fetcher.getFeeProgram().then(setFeeProgramInfoMap)
    fetcher.getFuturesFeeProgram().then(setFuturesFeeProgramInfoMap)
  }, [
    walletReady,
    setAccountInfo,
    setTradingVolumeInfo,
    setFeeProgramInfoMap,
    setFuturesFeeProgramInfoMap
  ])

  const TradingVolumeMarketTypeList = TRADING_VOLUME_MARKET_TYPE_LIST.map(
    key => {
      return (
        <div
          key={key}
          className={clsx('market-type noselect', {
            active: key === tradingVolumeMarketType
          })}
          onClick={() => setTradingVolumeMarketType(key)}
        >
          {MARKET_TYPE_LABEL_MAP[key]}
        </div>
      )
    }
  )
  const TradingVolumeInfoList = TRADING_VOLUME_INFO_LIST.map(key => {
    const data = tradingVolumeInfo[tradingVolumeMarketType] || {}
    const tradingVolume = data[key] || 0
    return (
      <div key={key} className="trading-volume-info-row">
        <div className="info-label">
          {TRADING_VOLUME_INFO_LABEL_MAP[key] || key}
        </div>
        <div className="info-value">
          {format(
            tradingVolume,
            2,
            Decimal(tradingVolume).gte(TRADING_VOLUME_COMPACT_THRESHOLD)
          )}
          <span className="info-currency">USDC</span>
        </div>
      </div>
    )
  })

  const MemberTierMarketTypeList = MARKET_TYPE_LIST.map(key => {
    return (
      <div
        key={key}
        className={clsx('market-type noselect', {
          active: key === memberTierMarketType
        })}
        onClick={() => setMemberTierMarketType(key)}
      >
        {MARKET_TYPE_LABEL_MAP[key]}
      </div>
    )
  })

  const accountInfoReady = accountInfo.account_id
  const isSpotMarketType = memberTierMarketType === 'SPOT'
  const maxTier = isSpotMarketType
    ? feeProgramMaxTier
    : futuresFeeProgramMaxTier
  const feeInfoMap =
    (isSpotMarketType ? feeProgramInfoMap : futuresFeeProgramInfoMap) || {}
  const currentTier =
    (isSpotMarketType ? accountInfo.tier : accountInfo.futures_tier) || 1
  const isMaxTier = Number(currentTier) === maxTier
  const nextTier = isMaxTier ? maxTier : Decimal(currentTier).add(1).toNumber()
  const currentTierInfo = feeInfoMap[currentTier] || {
    volume_max: 0,
    volume_min: 0
  }

  const nextTierInfo = feeInfoMap[nextTier] || {}
  const nextTierRequiredTradingVolume = !isMaxTier
    ? Decimal(currentTierInfo.volume_max)
        .sub(currentTierInfo.volume_min || 0)
        .toNumber()
    : 0
  const memberTierTradingVolumeInfo =
    tradingVolumeInfo[memberTierMarketType] || {}
  const memberTier30DaysTradingVolumeInfo =
    memberTierTradingVolumeInfo['30_DAYS'] || 0
  const memberTier30DaysFeesPaid =
    memberTierTradingVolumeInfo['30_DAYS_FEES_PAID'] || 0
  const currentTradingVolumeForNextTier = Decimal(
    memberTier30DaysTradingVolumeInfo
  ).sub(currentTierInfo.volume_min)
  const nextTierProgress = accountInfoReady
    ? isMaxTier
      ? 100
      : currentTradingVolumeForNextTier
          .div(nextTierRequiredTradingVolume)
          .times(100)
          .toNumber()
    : 0
  return (
    <div className="account-container">
      <TabNavigation />
      <div className={clsx('account', { card: !accountId })}>
        {accountId ? (
          <>
            {/*<div className="card account-block account-id-block">
              <div className="block-label">
                <div className="block-label-icon">
                  <IconColorWallet className="label-icon" />
                </div>
                Wallet Address
              </div>
              <div className="copy-address">
                <span className="address-highlight">{accountId}</span>
                <IconCopy
                  className="icon-copy"
                  onClick={() => {
                    copy(accountId)
                    setPopupNotification({
                      notification: {
                        key: Date.now(),
                        status: 'success',
                        message: 'Copied'
                      }
                    })
                  }}
                />
              </div>
            </div>*/}
            <div className="account-center-block">
              <div className="column-wrap">
                <div className="card account-block">
                  <div className="block-label divider">
                    <div className="block-label-icon">
                      <IconColorTradingVolume className="label-icon" />
                    </div>
                    Trading Volume
                  </div>
                  <div className="trading-volume-info">
                    <div className="market-type-selector">
                      {TradingVolumeMarketTypeList}
                    </div>
                    <div className="trading-volume">
                      {TradingVolumeInfoList}
                    </div>
                  </div>
                </div>
                {/*<AccountApiBlock revealApiKeyCallback={() => setShowApiTradingKeyPopup(true)} />*/}
                <WalletAddressBlock />
              </div>
              <div className="card account-block account-member-tier">
                <div className="block-label divider">
                  <div className="block-label-icon">
                    <IconColorUser className="label-icon" />
                  </div>
                  Member Tier
                  <div className="empty-fill-box" />
                  <div
                    className="member-tier-detail-text-button noselect"
                    onClick={() => setShowMemberTierPopup(true)}
                  >
                    Details
                  </div>
                </div>
                <div className="market-type-selector">
                  {MemberTierMarketTypeList}
                </div>
                <div className="member-tier-block">
                  <div className="member-tier">
                    <div className="current-tier">{currentTier || '-'}</div>
                    <div className="current-tier-label">Current Tier</div>
                  </div>
                  <div className="member-tier-progress">
                    <div className="tier-progress-bar-container">
                      <div className="current-tier-label">
                        <div>Tier {currentTier || '-'}</div>
                        <div>
                          <span className="trading-volume-label">
                            {isMaxTier ? 'Above' : ''}{' '}
                            {format(currentTierInfo.volume_min, 'max2')}
                          </span>
                          USDC
                        </div>
                      </div>
                      <div className="tier-progress-bar">
                        <div
                          className="tier-progress-detail"
                          style={{
                            left: `${isMaxTier ? 50 : nextTierProgress}%`
                          }}
                        >
                          <div className="detail-label">
                            30 Days Trading Volume
                          </div>
                          <div className="current-trading-volume">
                            <span>
                              {format(
                                memberTier30DaysTradingVolumeInfo,
                                2,
                                Decimal(memberTier30DaysTradingVolumeInfo).gte(
                                  TRADING_VOLUME_COMPACT_THRESHOLD
                                )
                              )}
                            </span>
                            <span>USDC</span>
                          </div>
                        </div>
                        <div
                          className="tier-progress-bar-indicator"
                          style={{ width: `${nextTierProgress}%` }}
                        />
                      </div>
                      {!isMaxTier && (
                        <div className="next-tier-label">
                          <div>Tier {currentTier ? nextTier : '-'}</div>
                          <div>
                            <span className="trading-volume-label">
                              {format(currentTierInfo.volume_max, 'max2')}
                            </span>
                            USDC
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="trading-fee-info">
                  <div className="section-title">Trading Fee</div>
                  <div className="current-fee-info">
                    <div className="fee-type">
                      <div className="fee-type-label">Maker</div>
                      <div className="fee-percentage">
                        {feeDisplayAdapter(currentTierInfo.maker_fee)}
                        <span className="fee-type-percentage-label">%</span>
                      </div>
                    </div>
                    <div className="next-tier-fee">
                      {!isMaxTier &&
                        `Next tier ${feeDisplayAdapter(
                          nextTierInfo.maker_fee
                        )} %`}
                    </div>
                    <div className="fee-type">
                      <div className="fee-type-label">Taker</div>
                      <div className="fee-percentage">
                        {feeDisplayAdapter(currentTierInfo.taker_fee)}
                        <span className="fee-type-percentage-label">%</span>
                      </div>
                    </div>
                    <div className="next-tier-fee">
                      {!isMaxTier &&
                        `Next tier ${feeDisplayAdapter(
                          nextTierInfo.taker_fee
                        )} %`}
                    </div>
                    <div className="total-fee-in-30-days">
                      <div>Paid in last 30 days</div>
                      <div className="total-fee">
                        {format(
                          memberTier30DaysFeesPaid,
                          2,
                          Decimal(memberTier30DaysFeesPaid).gte(
                            TRADING_VOLUME_COMPACT_THRESHOLD
                          )
                        )}
                        <span className="fee-currency-label">USDC</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="account-bottom-block">
              <div className="card account-block service-description">
                <div className="block-label divider">About BTSE</div>
                <div className="service-description-content">
                  Our responsibility is to provide the security and efficiency
                  you need to confidently invest in your financial independence.
                  Trading is a major use case for cryptocurrencies and a
                  deciding factor in the growth of the crypto ecosystem. It
                  allows for price discovery on a free market and creates direct
                  touchpoints with the future of money. Our aim is to integrate
                  digital assets with the existing financial world
                  infrastructure, leveraging cryptocurrencies’ decentralization
                  to increase convenience and accessibility. The technology is
                  here—it’s past time to connect it to the world.
                </div>
                <div className="empty-fill-box" />
                <div className="social-link-list">
                  <SocialIconLink
                    type="twitter"
                    link="https://twitter.com/BTSE_Official"
                  />
                  <SocialIconLink
                    type="discord"
                    link="https://discord.gg/btseofficial"
                  />
                </div>
              </div>
              <div className="card account-block service-description">
                <div className="block-label divider">About Orderly</div>
                <div className="service-description-content">
                  Orderly is a permissionless, decentralized exchange protocol
                  and modular ecosystem built on top of both EVM and NEAR
                  blockchains. It currently uses an off-chain order book to
                  provide a platform complete with a risk engine, matching
                  engine, and shared asset pools for dApps to build on top of,
                  with a goal of moving to a full on-chain mechanism. Orderly
                  aims to provide the most robust liquidity layer infrastructure
                  for any dApp to utilize and build on, boasting the highest
                  throughput and lowest latency in DeFi. With low fees, tight
                  spreads, and access to deep and aggregated liquidity from DeFi
                  and CeFi platforms, Orderly will be able to offer the best
                  user experience possible for an on-chain exchange.
                </div>
                <div className="empty-fill-box" />
                <div className="social-link-list">
                  <SocialIconLink
                    type="website"
                    link="https://orderly.network"
                  />
                  <SocialIconLink
                    type="twitter"
                    link="https://twitter.com/OrderlyNetwork"
                  />
                  <SocialIconLink
                    type="discord"
                    link="https://discord.com/invite/OrderlyNetwork"
                  />
                  <SocialIconLink
                    type="medium"
                    link="https://medium.com/@orderlynetwork"
                  />
                </div>
              </div>
            </div>
            {accountInfoReady && (
              <MemberTierPopup
                show={showMemberTierPopup}
                hidePopupCallback={() => setShowMemberTierPopup(false)}
                feeProgramMaxTier={maxTier}
                feeProgramInfoMap={feeInfoMap}
              />
            )}
            {walletReady && (
              <ApiTradingKeyPopup
                show={showApiTradingKeyPopup}
                hidePopupCallback={() => setShowApiTradingKeyPopup(false)}
              />
            )}
          </>
        ) : (
          <ConnectWalletView />
        )}
      </div>
    </div>
  )
}
